import { boardLanes, can, type QueueTicket, type Status, stageOf } from '@hrms/shared'
import { useMemo, useState } from 'react'
import { LuEyeOff, LuList, LuRefreshCw, LuSearchX, LuTicket } from 'react-icons/lu'
import { Link } from 'react-router'
import { useCurrentUser } from '../../../auth/AuthProvider.tsx'
import { Button, EmptyState, useToast } from '../../../components/ui/index.ts'
import { ApiError } from '../../../lib/api.ts'
import { useConfig } from '../../../lib/config.ts'
import { hint } from '../../../lib/hint.ts'
import { useMediaQuery } from '../../../lib/useMediaQuery.ts'
import { useNow } from '../../../lib/useNow.ts'
import { BoardLanes } from '../board/BoardLanes.tsx'
import { QueueFilterBar } from '../queue/QueueFilterBar.tsx'
import { boardSearchOf, useQueueParams } from '../queue/useQueueParams.ts'
import { useBoard, useBoardMove, useDirectory } from '../tickets/api.ts'
import { personName } from '../tickets/ui.tsx'
import { useOpenTicket } from '../tickets/useTicketParam.ts'
import { WaitPrompt } from '../tickets/WaitPrompt.tsx'
import { PageHeader } from './PageHeader.tsx'

function hiddenNotice(hidden: number) {
  return hidden === 1
    ? '1 confidential ticket is hidden. Only HR leads and the assigned HR partner can see it.'
    : `${hidden} confidential tickets are hidden. Only HR leads and the assigned HR partner can see them.`
}

/** HR board (DESIGN.md §3.2, PRD "HR experience: board"). */
export function BoardPage() {
  const user = useCurrentUser()
  const params = useQueueParams()
  const { filters, search } = params
  const boardSearch = useMemo(() => boardSearchOf(search), [search])
  const board = useBoard(boardSearch)
  const move = useBoardMove(boardSearch)
  const { data: config } = useConfig()
  const directory = useDirectory()
  const now = useNow()
  const openTicket = useOpenTicket()
  const toast = useToast()
  // Dragging needs a mouse or trackpad; on touch screens HR changes status in the drawer.
  const finePointer = useMediaQuery('(hover: hover) and (pointer: fine)')
  const editor = can(user, 'ticket.edit.fields')
  const canDrag = editor && finePointer
  const data = board.data
  const [waiting, setWaiting] = useState<{ ticket: QueueTicket; status: Status } | null>(null)

  const lanes = useMemo(
    () => (data && config ? boardLanes(config.statuses, data.columns) : []),
    [data, config],
  )

  const save = async (ticket: QueueTicket, status: Status, waitingMessage?: string) => {
    try {
      await move.mutateAsync({
        key: ticket.key,
        id: ticket.id,
        version: ticket.version,
        statusId: status.id,
        waitingMessage,
      })
      toast(`${ticket.key} moved to ${status.name}`)
      return true
    } catch (err) {
      // The ticket changed or vanished: the prompt's version is stale, so don't keep it open.
      if (err instanceof ApiError && [403, 404, 409].includes(err.status)) setWaiting(null)
      if (err instanceof ApiError && err.status === 409)
        toast(`Someone else just updated ${ticket.key}. The board now shows the latest version.`)
      else if (err instanceof ApiError && err.status === 404)
        toast(`${ticket.key} is no longer available to you.`)
      else if (err instanceof ApiError && err.status === 403)
        toast('Couldn’t move it. Your access may be view-only.')
      else toast(err instanceof Error && err.message ? err.message : 'Couldn’t move that ticket.')
      return false
    }
  }

  const onMove = (ticket: QueueTicket, status: Status) => {
    if (move.isPending) {
      toast('Still saving the last move. Try again in a moment.')
      return
    }
    if (!config) return
    // Into Waiting asks what's needed first; cancelling leaves the card where it was.
    if (status.stage === 'waiting' && stageOf(config.statuses, ticket.statusId) !== 'waiting')
      setWaiting({ ticket, status })
    else void save(ticket, status)
  }

  const countLine = data
    ? `${data.total} of ${data.overall} ${data.overall === 1 ? 'ticket' : 'tickets'} shown`
    : 'Loading tickets…'

  let body: React.ReactNode
  if (board.isError && !data)
    body = (
      <EmptyState
        icon={LuRefreshCw}
        color="amber"
        title="Couldn’t load the board"
        action={<Button onClick={() => void board.refetch()}>Try again</Button>}
      >
        Check your connection and try again.
      </EmptyState>
    )
  else if (!data || !config)
    body = (
      <div aria-busy="true" className="grid grid-flow-col gap-3 overflow-hidden">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-60 w-[260px] animate-pulse rounded-panel bg-surface-2" />
        ))}
        <span className="sr-only">Loading the board…</span>
      </div>
    )
  else if (data.overall === 0)
    body = (
      <EmptyState icon={LuTicket} color="violet" title="No tickets yet">
        Tickets employees raise will show up here, one column per status.
      </EmptyState>
    )
  else if (data.total === 0)
    body = (
      <EmptyState
        icon={LuSearchX}
        color="slate"
        title="No tickets match these filters"
        action={<Button onClick={params.clearFilters}>Clear filters</Button>}
      >
        Try widening the date range or removing a filter.
      </EmptyState>
    )
  else
    body = (
      <BoardLanes
        lanes={lanes}
        people={data.people}
        config={config}
        now={now}
        onOpen={openTicket}
        onMove={canDrag ? onMove : undefined}
      />
    )

  const hidden = data?.hiddenConfidential ?? 0
  const raiser = waiting ? personName(data?.people ?? {}, waiting.ticket.raisedBy) : ''
  return (
    <>
      <PageHeader
        title="Board"
        description={countLine}
        actions={
          <Link
            to={{ pathname: '/hr/tickets', search: boardSearch ? `?${boardSearch}` : '' }}
            data-tour="view-switch"
            className="inline-flex h-[34px] items-center gap-1.5 rounded-ctl border border-border-strong bg-surface px-3.5 font-medium hover:bg-surface-2"
            {...hint('Same filters, as a sortable list')}
          >
            <LuList aria-hidden className="size-4 text-violet-fg" />
            List view
          </Link>
        }
      />
      {config && (
        <QueueFilterBar
          filters={filters}
          setFilters={params.setFilters}
          clearFilters={params.clearFilters}
          config={config}
          people={directory.data?.people ?? []}
          timeZone={directory.data?.timeZone}
          now={now}
          hideStatus
        />
      )}
      {(hidden > 0 || editor) && (
        <div className="mb-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-[13px] text-muted">
          {editor && (
            <p>
              {canDrag
                ? 'Drag a card to another column to change its status.'
                : 'Open a card to change its status.'}
            </p>
          )}
          {hidden > 0 && (
            <p className="flex items-center gap-1.5">
              <LuEyeOff aria-hidden className="size-4 shrink-0 text-amber-fg" />
              {hiddenNotice(hidden)}
            </p>
          )}
        </div>
      )}
      {body}
      {waiting && (
        <WaitPrompt
          ticketKey={waiting.ticket.key}
          firstName={raiser.split(' ')[0] || 'the employee'}
          statusName={waiting.status.name}
          onCancel={() => setWaiting(null)}
          onSubmit={async (message) => {
            const ok = await save(waiting.ticket, waiting.status, message)
            if (ok) setWaiting(null)
            return ok
          }}
        />
      )}
    </>
  )
}
